import { useState, type FormEvent } from "react";
import s from "./Projects.module.css";
import { useSession } from "../../app/SessionContext";
import { useAddProjectLogEntry, useCloseProjectAction, useProjectLog } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { LOG_KIND_LABEL, LOG_KINDS, type LogKind, type ProjectLogEntry } from "../../domain/projects";
import { Button, Card, EmptyState, ErrorState, Input, Segmented, Skeleton } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { t, tn } from "../../i18n";

type Show = "all" | LogKind | "open";

/**
 * The project's log: notes, progress updates and actions, newest first.
 *
 * Nothing here edits an entry. A note or an update is permanent — a
 * correction is a new entry and the old one stays — and an action's only
 * change is its closure, done or cancelled with a reason. Progress is the
 * last figure somebody reported, shown with who and when; the screen does not
 * work one out from dates or money.
 */
export function ProjectLogCard({ projectId }: { projectId: string }) {
  const { principal } = useSession();
  const log = useProjectLog(projectId);
  const [show, setShow] = useState<Show>("all");
  const canWrite = hasPermission(principal, PERMISSION.projectsWrite);

  const entries = (log.data?.entries ?? []).filter((e) =>
    show === "all" ? true : show === "open" ? e.status === "open" : e.kind === show,
  );

  return (
    <Card
      title="Project log"
      sub="notes, progress updates and actions — permanent once recorded; actions close as done or canceled"
      actions={
        log.data && (
          <div className={s.logCounts}>
            {log.data.latestProgress && (
              <span
                title={
                  log.data.latestProgress.reportedBy
                    ? t("Reported {date} by {name}", {
                        date: formatDateTime(log.data.latestProgress.reportedAt),
                        name: log.data.latestProgress.reportedBy,
                      })
                    : t("Reported {date}", { date: formatDateTime(log.data.latestProgress.reportedAt) })
                }
              >
                <b>{log.data.latestProgress.pct}%</b> {t("reported")}
              </span>
            )}
            <span>
              <b>{log.data.openActions}</b> {tn(log.data.openActions, "open action", "open actions")}
            </span>
            {log.data.overdueActions > 0 && (
              <span className={s.bad}>
                <b>{log.data.overdueActions}</b> {t("overdue")}
              </span>
            )}
          </div>
        )
      }
    >
      {log.data?.latestProgress && (
        <div className={s.progress} aria-label={t("Progress reported: {pct}%", { pct: log.data.latestProgress.pct })}>
          <span className={s.progressFill} style={{ width: `${log.data.latestProgress.pct}%` }} />
        </div>
      )}
      {canWrite ? (
        <NewEntry projectId={projectId} />
      ) : (
        <div className={s.said}>
          {t("Reading only — adding to the log needs {permission}.", { permission: PERMISSION.projectsWrite })}
        </div>
      )}
      <div style={{ margin: "12px 0 6px" }}>
        <Segmented<Show>
          ariaLabel="Show"
          value={show}
          options={[
            { value: "all", label: "All" },
            { value: "note", label: "Notes" },
            { value: "update", label: "Updates" },
            { value: "action", label: "Actions" },
            { value: "open", label: "Open actions" },
          ]}
          onChange={setShow}
        />
      </div>
      {log.isPending ? (
        <Skeleton rows={3} />
      ) : log.isError ? (
        <ErrorState error={log.error} retry={() => void log.refetch()} />
      ) : entries.length === 0 ? (
        <EmptyState>
          {log.data.entries.length ? "Nothing of that kind yet." : "Nothing recorded on this project yet."}
        </EmptyState>
      ) : (
        <ol className={s.log}>
          {entries.map((e) => (
            <Entry key={e.id} entry={e} projectId={projectId} canWrite={canWrite} />
          ))}
        </ol>
      )}
    </Card>
  );
}

function NewEntry({ projectId }: { projectId: string }) {
  const add = useAddProjectLogEntry(projectId);
  const [kind, setKind] = useState<LogKind>("note");
  const [body, setBody] = useState("");
  const [pct, setPct] = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const pctValue = pct === "" ? undefined : Number(pct);
  const pctBad = pctValue != null && !(Number.isInteger(pctValue) && pctValue >= 0 && pctValue <= 100);
  const ready = body.trim() && !pctBad && (kind !== "action" || owner.trim());

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    add.mutate(
      {
        kind,
        body: body.trim(),
        progressPct: kind === "update" ? pctValue : undefined,
        owner: kind === "action" ? owner.trim() : undefined,
        dueDate: kind === "action" && due ? due : undefined,
      },
      {
        onSuccess: () => {
          setBody("");
          setPct("");
          setOwner("");
          setDue("");
        },
      },
    );
  };

  return (
    <form className={s.logForm} onSubmit={submit} aria-label={t("Add to the project log")}>
      <Segmented<LogKind>
        ariaLabel="Entry kind"
        value={kind}
        options={LOG_KINDS.map((k) => ({ value: k, label: LOG_KIND_LABEL[k] }))}
        onChange={setKind}
      />
      <textarea
        className={s.logBody}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        placeholder={
          kind === "note"
            ? t("What happened — a meeting, a decision, a site observation")
            : kind === "update"
              ? t("Progress this period")
              : t("What needs doing")
        }
        aria-label={t("Entry text")}
      />
      <div className={s.logRow}>
        {kind === "update" && (
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            placeholder={t("% complete (optional)")}
            aria-label={t("Percent complete")}
            aria-invalid={pctBad}
            style={{ width: 170 }}
          />
        )}
        {kind === "action" && (
          <>
            <Input
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder={t("Owner (required)")}
              aria-label={t("Owner")}
              style={{ width: 200 }}
            />
            <Input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              aria-label={t("Due date")}
              style={{ width: 160 }}
            />
          </>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={!ready || add.isPending}
          style={{ marginInlineStart: "auto" }}
        >
          {add.isPending ? t("Recording…") : t("Record {kind}", { kind: t(LOG_KIND_LABEL[kind]).toLowerCase() })}
        </Button>
      </div>
      {add.isError && (
        <div className={s.bad} role="alert">
          {t("Refused: {message}", { message: (add.error as Error).message })}
        </div>
      )}
    </form>
  );
}

function Entry({ entry: e, projectId, canWrite }: { entry: ProjectLogEntry; projectId: string; canWrite: boolean }) {
  const close = useCloseProjectAction(projectId);
  const [cancelling, setCancelling] = useState(false);
  const [note, setNote] = useState("");

  return (
    <li className={s.logEntry} data-kind={e.kind}>
      <div className={s.logHead}>
        <span className={s.logKind}>{t(LOG_KIND_LABEL[e.kind])}</span>
        {e.progressPct != null && <b>{e.progressPct}%</b>}
        {e.kind === "action" && (
          <span className={e.overdue ? s.bad : e.status === "open" ? undefined : s.said} style={{ margin: 0 }}>
            {e.status === "open"
              ? e.overdue
                ? t("OVERDUE")
                : t("OPEN")
              : e.status === "done"
                ? t("DONE")
                : t("CANCELLED")}
          </span>
        )}
        <span className={s.logMeta}>
          {e.createdBy ?? t("unrecorded")} · {formatDateTime(e.createdAt)}
        </span>
      </div>
      <div className={s.logText}>{e.body}</div>
      {e.kind === "action" && (
        <div className={s.logMeta}>
          {t("Owner")} <b>{e.owner}</b>
          {e.dueDate ? ` · ${t("due {date}", { date: formatDate(e.dueDate) })}` : ` · ${t("no due date")}`}
          {e.closedAt &&
            ` · ${t("{status} {date}", { status: t(e.status ?? ""), date: formatDateTime(e.closedAt) })}${e.closedBy ? ` ${t("by {name}", { name: e.closedBy })}` : ""}`}
          {e.closureNote && ` — ${e.closureNote}`}
        </div>
      )}
      {e.status === "open" && canWrite && (
        <div className={s.logRow}>
          {cancelling ? (
            <>
              <Input
                value={note}
                onChange={(ev) => setNote(ev.target.value)}
                placeholder={t("Why it is being canceled (required)")}
                aria-label={t("Cancellation note")}
                style={{ flex: 1 }}
                autoFocus
              />
              <Button
                size="sm"
                variant="danger"
                disabled={!note.trim() || close.isPending}
                onClick={() => close.mutate({ entryId: e.id, outcome: "cancelled", note: note.trim() })}
              >
                {t("Cancel action")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCancelling(false)}>
                {t("Back")}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="primary"
                disabled={close.isPending}
                onClick={() => close.mutate({ entryId: e.id, outcome: "done" })}
              >
                {t("Mark done")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
                {t("Cancel…")}
              </Button>
            </>
          )}
        </div>
      )}
      {close.isError && (
        <div className={s.bad} role="alert">
          {t("Refused: {message}", { message: (close.error as Error).message })}
        </div>
      )}
    </li>
  );
}
